import {useCallback, useEffect, useMemo, useState} from 'react';

import {getAccountProvisioningHistory} from 'src/openapi/admin/apis/GetAccountUserHistoryByTypeApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {getPresetRange} from '../user-history-chart/chart-period';

import type {InitiatorValue} from './initiator-suggest';
import type {RangePreset} from '../user-history-chart/chart-period';
import type {AccountProvisioningHistoryEvent} from 'src/openapi/admin/models/AccountProvisioningHistoryEvent';
import type {AccountProvisioningHistoryRequest} from 'src/openapi/admin/models/AccountProvisioningHistoryRequest';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {ProductHistorySeries} from 'src/openapi/admin/models/ProductHistorySeries';

export type ProvisioningFilters = Omit<
    AccountProvisioningHistoryRequest, 'cursor' | 'pageSize' | 'initiatorSystem' | 'initiatorUserId'
> & {initiator?: InitiatorValue, period?: RangePreset | 'Custom'};
export type AdvancedFilters = Omit<ProvisioningFilters, 'email'>;
export type AdvancedFilterKey = Exclude<keyof AdvancedFilters, 'period'>;
export type ProvisioningPageSize = 25 | 50 | 100 | 250;
type Page = {
    rows: AccountProvisioningHistoryEvent[],
    products: ProductHistorySeries[],
    nextCursor?: string | null,
};
type Query = {
    accountId: number, filters: ProvisioningFilters, page: number, pageSize: ProvisioningPageSize,
    cursor?: string, cache: Map<number, Page>,
};
type Result = Page & {
    query: Query,
    error: ApiError | null,
};

const initialQuery = (accountId: number, filters: ProvisioningFilters): Query => (
    {accountId, filters, page: 1, pageSize: 25, cache: new Map()}
);
const resetPages = (query: Query): Query => ({...query, page: 1, cursor: undefined, cache: new Map()});
const TEXT_FILTERS = new Set(['reason', 'email']);
const MAX_TEXT_FILTER_LENGTH = 60;
const normalize = (
    filters: ProvisioningFilters, previous: ProvisioningFilters,
): ProvisioningFilters => Object.fromEntries(
    Object.entries(filters).map(([key, value]) => {
        const trimmed = typeof value === 'string' ? value.trim() : value;
        const tooShort = TEXT_FILTERS.has(key) && typeof trimmed === 'string' && trimmed && trimmed.length < 3;
        const limited = TEXT_FILTERS.has(key) && typeof trimmed === 'string'
            ? trimmed.slice(0, MAX_TEXT_FILTER_LENGTH) : trimmed;
        return [key, tooShort ? previous[key as keyof ProvisioningFilters] : limited];
    }).filter(([, value]) => value),
);
const withFilters = (query: Query, filters: ProvisioningFilters): Query => {
    const normalized = normalize(filters, query.filters);
    const unchanged = isDeepEqual({prev: query.filters, current: normalized});
    return unchanged
        ? query : resetPages({...query, filters: normalized});
};

export const useProvisioningHistory = ({accountId}: {accountId: number}) => {
    const dispatch = useAppDispatch();
    const today = new Date().toISOString().slice(0, 10);
    const defaultFilters = useMemo<AdvancedFilters>(() => {
        const {startDate, endDate} = getPresetRange('Current month', today);
        return {startDate, endDate, period: 'Current month'};
    }, [today]);
    const [query, setQuery] = useState(() => initialQuery(accountId, defaultFilters));
    const [result, setResult] = useState<Result | null>(null);
    const [emailResetKey, setEmailResetKey] = useState(0);

    if (query.accountId !== accountId) {
        setQuery(initialQuery(accountId, defaultFilters));
        setEmailResetKey(key => key + 1);
    }

    useEffect(() => {
        const cached = query.cache.get(query.page);
        if (cached) {
            setResult({query, ...cached, error: null});
            return;
        }
        const controller = new AbortController();
        const load = async () => {
            const {initiator, period, ...filters} = query.filters;
            const {payload} = await dispatch(getAccountProvisioningHistory({
                accountId: query.accountId,
                accountProvisioningHistoryRequest: {
                    ...filters,
                    ...(initiator?.kind === 'user' ? {initiatorUserId: initiator.id} : {}),
                    ...(initiator?.kind === 'system' ? {initiatorSystem: initiator.system} : {}),
                    ...(query.cursor ? {cursor: query.cursor} : {}),
                    pageSize: query.pageSize as unknown as AccountProvisioningHistoryRequest['pageSize'],
                },
            }, {signal: controller.signal}));
            if (!controller.signal.aborted) {
                if (!payload.error) {
                    query.cache.set(query.page, {
                        rows: payload.data.data, products: payload.data.products, nextCursor: payload.data.nextCursor,
                    });
                }
                setResult(previous => ({
                    query,
                    products: payload.error ? previous?.products ?? [] : payload.data.products,
                    rows: payload.error ? [] : payload.data.data,
                    nextCursor: payload.error ? undefined : payload.data.nextCursor,
                    error: payload.error ? payload.data : null,
                }));
            }
        };
        load();
        return () => controller.abort();
    }, [dispatch, query]);

    const setEmail = useCallback((email: string) => {
        const normalizedEmail = email.trim().slice(0, MAX_TEXT_FILTER_LENGTH);
        setQuery(prev => {
            const next = withFilters(prev, {...prev.filters, email: normalizedEmail});
            const retryFailedQuery = next === prev && result?.query === prev && result.error
                && normalizedEmail === (prev.filters.email ?? '');
            return retryFailedQuery ? {...prev} : next;
        });
    }, [result]);
    const retry = useCallback(() => {
        setQuery(prev => result?.query === prev && result.error ? {...prev} : prev);
    }, [result]);
    const setAdvancedFilters = useCallback((filters: AdvancedFilters) => {
        setQuery(prev => withFilters(prev, {...filters, email: prev.filters.email}));
    }, []);
    const removeFilter = useCallback((key: AdvancedFilterKey) => {
        setQuery(prev => withFilters(prev, {
            ...prev.filters, [key]: undefined,
            ...(key === 'startDate' || key === 'endDate' ? {period: undefined} : {}),
        }));
    }, []);
    const clearFilters = useCallback(() => {
        setEmailResetKey(key => key + 1);
        setQuery(prev => withFilters(prev, {}));
    }, []);
    const nextPage = useCallback(() => {
        setQuery(prev => result?.query === prev && !result.error && result.rows.length > prev.pageSize
            && result.nextCursor ? {...prev, page: prev.page + 1, cursor: result.nextCursor} : prev);
    }, [result]);
    const previousPage = useCallback(() => {
        setQuery(prev => result?.query === prev && prev.page > 1
            ? {...prev, page: prev.page - 1, cursor: undefined} : prev);
    }, [result]);
    const restart = useCallback(() => setQuery(resetPages), []);
    const setPageSize = useCallback((pageSize: ProvisioningPageSize) => {
        setQuery(prev => prev.pageSize === pageSize ? prev : resetPages({...prev, pageSize}));
    }, []);

    const current = result?.query === query ? result : null;
    return {
        rows: current?.rows.slice(0, query.pageSize) ?? [],
        products: result?.products ?? [],
        hasNextPage: Boolean(current && !current.error && current.rows.length > query.pageSize && current.nextCursor),
        hasPreviousPage: query.page > 1,
        canRestart: Boolean(current?.error && query.cursor),
        isLoading: !current,
        error: current?.error ?? null,
        filters: query.filters, page: query.page, pageSize: query.pageSize, emailResetKey,
        defaultFilters,
        hasFilters: Object.entries(query.filters).some(([key, value]) => key !== 'period' && Boolean(value)),
        setEmail, setAdvancedFilters, removeFilter, clearFilters, nextPage, previousPage, setPageSize, retry, restart,
    };
};
